import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  DocumentTextIcon,
  FolderIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';
import { superAdminAPI } from '../../services/api';

/**
 * Dashboard for Content Generator role
 * Shows assigned KITs, question creation tools, and personal statistics
 */
const ContentGeneratorDashboard = () => {
  const [stats, setStats] = useState({
    totalQuestionsCreated: 0,
    pendingQuestions: 0,
    readyToShipQuestions: 0,
    questionsWithIssues: 0,
    assignedQuestions: 0
  });
  const [assignedKits, setAssignedKits] = useState([]);
  const [recentQuestions, setRecentQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch Content Generator statistics
      const [statsResponse, kitsResponse, questionsResponse] = await Promise.all([
        superAdminAPI.getContentGeneratorStats(),
        superAdminAPI.getContentGeneratorKits(),
        superAdminAPI.getContentGeneratorQuestions({ page: 0, size: 5 })
      ]);

      setStats(statsResponse.data);
      setAssignedKits(kitsResponse.data);
      setRecentQuestions(questionsResponse.data.content || []);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const quickActions = [
    { name: 'Create Question', href: '/questions/create', icon: PlusIcon, color: 'bg-green-500' },
    { name: 'Upload Excel', href: '/questions/bulk-upload', icon: ArrowUpTrayIcon, color: 'bg-blue-500' },
    { name: 'PDF to MCQ', href: '/questions/pdf-to-mcq', icon: DocumentArrowUpIcon, color: 'bg-purple-500' },
    { name: 'My Questions', href: '/my-questions', icon: DocumentTextIcon, color: 'bg-indigo-500' }
  ];

  const questionTypes = [
    { name: 'Multiple Choice', href: '/questions/create?type=mcq', description: 'Single or multiple correct answers' },
    { name: 'True/False', href: '/questions/create?type=true-false', description: 'Binary choice questions' },
    { name: 'Fill in Blank', href: '/questions/create?type=fill-blank', description: 'Text input questions' },
    { name: 'Essay/Text', href: '/questions/create?type=essay', description: 'Long form answers' }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Content Generator Dashboard</h1>
        <p className="text-gray-600 mt-1">Create and manage questions for assigned KITs</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {quickActions.map((action) => (
          <Link
            key={action.name}
            to={action.href}
            className="group relative bg-white p-6 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center">
              <div className={`${action.color} p-3 rounded-lg`}>
                <action.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                  {action.name}
                </h3>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.totalQuestionsCreated}</div>
          <div className="text-sm text-gray-600">Total Created</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-yellow-600">{stats.pendingQuestions}</div>
          <div className="text-sm text-gray-600">Pending Review</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-green-600">{stats.readyToShipQuestions}</div>
          <div className="text-sm text-gray-600">Ready to Ship</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-red-600">{stats.questionsWithIssues}</div>
          <div className="text-sm text-gray-600">Need Revision</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-blue-600">{stats.assignedQuestions}</div>
          <div className="text-sm text-gray-600">Assigned to Me</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Question Creation Tools */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Create Questions</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-3">
              {questionTypes.map((type) => (
                <Link
                  key={type.name}
                  to={type.href}
                  className="block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="font-medium text-gray-900">{type.name}</div>
                  <div className="text-sm text-gray-600 mt-1">{type.description}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Popular Tags */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Popular Tags</h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 font-medium text-sm">#</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">mathematics</div>
                    <div className="text-sm text-gray-600">Basic math concepts</div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">12 questions</div>
              </div>

              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-green-600 font-medium text-sm">#</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">geography</div>
                    <div className="text-sm text-gray-600">World geography topics</div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">8 questions</div>
              </div>

              <Link to="/tags" className="block text-center text-sm text-blue-600 hover:text-blue-800 mt-3">
                View all tags →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Questions */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Recent Questions</h3>
            <Link to="/my-questions" className="text-sm text-blue-600 hover:text-blue-800">
              View all →
            </Link>
          </div>
        </div>
        <div className="p-6">
          {recentQuestions.length > 0 ? (
            <div className="space-y-4">
              {recentQuestions.map((question) => (
                <div key={question.id} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 mb-1">
                      {question.questionText.substring(0, 100)}
                      {question.questionText.length > 100 && '...'}
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>{question.subject}</span>
                      <span>•</span>
                      <span>{question.difficultyLevel}</span>
                      <span>•</span>
                      <span>{new Date(question.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    {question.status === 'PENDING' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        Pending
                      </span>
                    )}
                    {question.status === 'READY_TO_SHIP' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Approved
                      </span>
                    )}
                    {['RENDERING_FAILURE', 'DUPLICATE', 'NOT_USABLE'].includes(question.status) && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                        Needs Fix
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No questions created yet</p>
          )}
        </div>
      </div>

      {/* Workflow Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-3">💡 Content Creation Tips</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <h4 className="font-medium mb-2">Question Quality</h4>
            <ul className="space-y-1">
              <li>• Use clear, concise language</li>
              <li>• Include proper explanations</li>
              <li>• Verify correct answers</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Workflow</h4>
            <ul className="space-y-1">
              <li>• Submit for review when ready</li>
              <li>• Address reviewer feedback promptly</li>
              <li>• Use appropriate difficulty levels</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentGeneratorDashboard;
